import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useEffect, useState } from 'react';
import {
    normalizeReportModalSummaryData,
    type ReportModalSummaryData,
} from '@/domain/reporting/reporting-modal-summary';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { RuntimeSupportedMarketplace } from '@/shared/marketplace';

type UseProductsReportModalSummaryParams = {
    isShown: boolean;
    accessToken: string;
    periodFrom: string;
    periodTo: string;
    selectedPlatforms?: RuntimeSupportedMarketplace[];
    selectedProductGroupIds?: string[];
    selectedBrandGroupIds?: string[];
    selectedReviewFeedItemIds?: string[];
    emptySummaryData: ReportModalSummaryData;
    refreshToken?: number;
};

export type ReportModalSummaryState = ReportModalSummaryData & {
    isSummaryLoaded: boolean;
    summaryProcessedDays: number;
    summaryTotalDays: number;
};

const productsReportSummaryLogger = createClientLogger({
    scope: 'products-report-summary',
});

export const useProductsReportModalSummary = ({
    isShown,
    accessToken,
    periodFrom,
    periodTo,
    selectedPlatforms,
    selectedProductGroupIds,
    selectedBrandGroupIds,
    selectedReviewFeedItemIds,
    emptySummaryData,
    refreshToken,
}: UseProductsReportModalSummaryParams): ReportModalSummaryState => {
    const { reviewsApi } = useMarketplaceApi();
    const [summaryState, setSummaryState] = useState<ReportModalSummaryState>(() => ({
        ...emptySummaryData,
        isSummaryLoaded: false,
        summaryProcessedDays: 0,
        summaryTotalDays: 0,
    }));

    useEffect(() => {
        if (!isShown) {
            return;
        }

        const controller = new AbortController();
        setSummaryState((prev) => ({
            ...prev,
            isSummaryLoaded: false,
            summaryProcessedDays: 0,
            summaryTotalDays: 0,
        }));

        reviewsApi
            .getReportSummary(
                {
                    fromDate: periodFrom || undefined,
                    toDate: periodTo || undefined,
                    platform: selectedPlatforms,
                    productGroupIds: selectedProductGroupIds,
                    brandGroupIds: selectedBrandGroupIds,
                    reviewFeedItemIds: selectedReviewFeedItemIds,
                },
                {
                    authToken: accessToken,
                    signal: controller.signal,
                    onProgress: (progress) => {
                        setSummaryState((prev) => ({
                            ...prev,
                            summaryProcessedDays: progress.processedDays,
                            summaryTotalDays: progress.totalDays,
                        }));
                    },
                },
            )
            .then((summaryResponse) => {
                setSummaryState((prev) => ({
                    ...normalizeReportModalSummaryData(summaryResponse.data),
                    isSummaryLoaded: true,
                    summaryProcessedDays: prev.summaryProcessedDays,
                    summaryTotalDays: prev.summaryTotalDays,
                }));
            })
            .catch((error: unknown) => {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: productsReportSummaryLogger,
                    message: '[client.products-report.summary-load] Failed to load products report summary.',
                    error,
                    context: {
                        periodFrom,
                        periodTo,
                        selectedPlatforms,
                        selectedProductGroupIds,
                        selectedBrandGroupIds,
                        selectedReviewFeedItemIds,
                    },
                });
                setSummaryState({
                    ...emptySummaryData,
                    isSummaryLoaded: true,
                    summaryProcessedDays: 0,
                    summaryTotalDays: 0,
                });
            });

        return () => {
            controller.abort();
        };
    }, [
        accessToken,
        emptySummaryData,
        isShown,
        periodFrom,
        periodTo,
        selectedBrandGroupIds,
        selectedPlatforms,
        selectedProductGroupIds,
        selectedReviewFeedItemIds,
        refreshToken,
    ]);

    return summaryState;
};
